// Work item reference — what a "Work item reference" field (schema_custom
// com.zebra.stride:workitemref) draws on the work item screen.
//
// The value is ONE work item's key, written by automations (custom pages,
// scripts, the API) — so this is a read-only card, never an input. One card,
// top to bottom, joined by a thin tree line:
//
//   PROJECT  the referenced work item's project, its full name as plain text
//            (never its avatar)
//   ↳ each work item ABOVE the referenced one, top first — slightly quieter
//     ↳ the referenced work item itself — the field's value, the most prominent
//
// Every work item level shows the same things: its type icon, its key (a link
// that opens it), its Status, its Summary, how far it is ("4 of 7 done") and
// its Due date, red once it has passed while the work is still open. For the
// referenced work item "how far" is the work items carrying the SAME key in
// the SAME field; for a work item above it, that work item and everything
// under it.
//
// One call per card: GET /api/work-item-ref/:fieldId/:key
// (server/work-item-ref-handler.js). A key the reader may not see reads as
// not found, exactly like one that does not exist; the chain above stops at
// the first work item the reader may not see (the server never sends it).

import React, { useEffect, useState } from 'react';
import api from '@app/services/api';
import { client } from '@platform/http/api-client';
import { formatDate, formatIfCanonicalDate, useDateFormatVersion } from '@platform/app-shell/frontend/utils/dateFormat';
import { issueResultsHref, handleIssueLinkActivation } from '@modules/zebra-search/frontend/utils/issueLinks';
import { refKeyOf, statusClassFor, progressOf, dueInfo, treeIndents } from '@modules/database/work-items/frontend/components/WorkItemRefField/workItemRef';
import '@modules/database/work-items/frontend/components/WorkItemRefField/WorkItemRefField.css';

const enc = encodeURIComponent;
const typeIconUrl = (id) => api.getJiraImageUrl(`/api/icons/work-type/${enc(String(id))}`);
const showDate = (day) => formatIfCanonicalDate(day) ?? formatDate(day, 'date', day);

// One work item level: head (icon, key, Status), then Summary, progress and
// Due date under it.
function WorkItemLevel({ item, progress: rawProgress, onOpenIssue, today }) {
  const isDone = item.status?.categoryKey === 'done';
  const progress = progressOf(rawProgress);
  const due = dueInfo(item.duedate, { done: isDone, ...(today ? { today } : {}) });
  return (
    <>
      <div className="wiref-head">
        {item.workTypeId
          ? <img className="wiref-icon" src={typeIconUrl(item.workTypeId)} alt="" width="16" height="16" />
          : <span className="wiref-icon" />}
        <a
          className="linked-issue-key-link wiref-key"
          href={issueResultsHref(item.key)}
          // With no host to open it in place, the link simply follows its href.
          onClick={onOpenIssue ? (e) => handleIssueLinkActivation(e, onOpenIssue, item.key) : undefined}
        >
          {item.key}
        </a>
        {item.status && (
          <span className={`status-badge status-${statusClassFor(item.status.categoryKey)} wiref-status`}>
            <span className="wiref-status-name">{item.status.name}</span>
          </span>
        )}
      </div>
      {(item.summary || progress || due) && (
        <div className="wiref-body">
          {item.summary && <div className="wiref-summary">{item.summary}</div>}
          {progress && (
            <div className="wiref-progress">
              <div
                className="wiref-progress-bar"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={progress.total}
                aria-valuenow={progress.done}
                aria-valuetext={progress.label}
              >
                <div className="wiref-progress-fill" style={{ width: `${progress.pct}%` }} />
              </div>
              <span className="wiref-progress-label">{progress.label}</span>
            </div>
          )}
          {due && (
            <div className="wiref-due">
              <span className="wiref-due-label">Due date</span>
              <span className={`wiref-due-date${due.late ? ' is-late' : ''}`}>{showDate(item.duedate)}</span>
              {due.relative && <span className={`wiref-due-rel${due.late ? ' is-late' : ''}`}>{due.relative}</span>}
            </div>
          )}
        </div>
      )}
    </>
  );
}

// Where a row of the tree sits: its own indent and the indent of the row above
// (the line comes down from that row's icon) — CSS custom properties, so the
// stylesheet draws the lines.
const indentStyle = ({ at, from }) => ({ '--wiref-at': `${at}px`, ...(from != null ? { '--wiref-from': `${from}px` } : {}) });

// The card for a state the field is in — drawn on its own so the suite can
// render every state without a network:
//   { kind: 'empty' }                       nothing stored
//   { kind: 'loading' }                     asking the server
//   { kind: 'missing', text }               no such key, or not one the reader may see
//   { kind: 'failed', text }                the server could not answer
//   { kind: 'ready', item, progress,        the work item and its family,
//     project?, ancestors? }                its project and the work items above it
export function WorkItemRefCard({ state, onOpenIssue = null, today = undefined }) {
  if (!state || state.kind === 'empty') return <span className="wiref-muted">—</span>;
  if (state.kind === 'loading') return <span className="wiref-skeleton" role="status" aria-label="Loading" />;
  if (state.kind === 'missing') return <span className="wiref-muted">{`${state.text} (not found)`}</span>;
  if (state.kind === 'failed') return <span className="wiref-muted">{`${state.text} (could not load)`}</span>;

  const project = state.project?.name ? state.project : null;
  const above = (Array.isArray(state.ancestors) ? state.ancestors : []).filter((a) => a && a.key);
  // Top to bottom: every work item above (top first), then the referenced one.
  const levels = [
    ...above.map((a) => ({ item: a, progress: a.progress, isValue: false })),
    { item: state.item, progress: state.progress, isValue: true },
  ];
  const isTree = !!project || above.length > 0;
  const indents = treeIndents(levels.length, { withProject: !!project });
  return (
    <div className={`wiref-card${isTree ? ' is-tree' : ''}`}>
      {project && (
        <div className="wiref-node wiref-project has-next" style={indentStyle(indents.project)}>
          <span className="wiref-caption">Project</span>
          <span className="wiref-project-name">{project.name}</span>
        </div>
      )}
      {levels.map((level, i) => {
        const place = indents.levels[i];
        // A row past the indent cap sits straight under the one above: the
        // trunk above runs into its icon, no elbow.
        const cls = [
          'wiref-node', 'wiref-level', level.isValue ? 'is-value' : 'is-above',
          place.from != null && place.at > place.from ? 'has-elbow' : '', i < levels.length - 1 ? 'has-next' : '',
        ].filter(Boolean).join(' ');
        return (
          <div key={`${i}:${level.item.key}`} className={cls} style={isTree ? indentStyle(place) : undefined}>
            <WorkItemLevel item={level.item} progress={level.progress} onOpenIssue={onOpenIssue} today={today} />
          </div>
        );
      })}
    </div>
  );
}

// The field on a screen: reads the card for the key its value names.
//   fieldId      the field (customfield_N) — the family is counted in it
//   value        the stored value (a key)
//   onOpenIssue  opens a work item (the panel's key click)
export default function WorkItemRefField({ fieldId, value, onOpenIssue = null }) {
  useDateFormatVersion(); // the Due date follows the admin's date format
  const { text, key } = refKeyOf(value);
  const [answer, setAnswer] = useState(null); // { for: key, item, progress, project, ancestors } | { for: key, failed: true }

  useEffect(() => {
    if (!key || !fieldId) return undefined;
    let alive = true;
    client.get(`/api/work-item-ref/${enc(fieldId)}/${enc(key)}`)
      .then((r) => {
        if (!alive) return;
        setAnswer({
          for: key,
          item: r.data?.item || null,
          progress: r.data?.progress || null,
          project: r.data?.project || null,
          ancestors: Array.isArray(r.data?.ancestors) ? r.data.ancestors : [],
        });
      })
      .catch(() => { if (alive) setAnswer({ for: key, failed: true }); });
    return () => { alive = false; };
  }, [fieldId, key]);

  let state;
  if (!text) state = { kind: 'empty' };
  else if (!key) state = { kind: 'missing', text };
  // An answer for another key (the value just changed) is never shown.
  else if (!answer || answer.for !== key) state = { kind: 'loading' };
  else if (answer.failed) state = { kind: 'failed', text: key };
  else if (!answer.item) state = { kind: 'missing', text: key };
  else state = { kind: 'ready', item: answer.item, progress: answer.progress, project: answer.project, ancestors: answer.ancestors };

  return <WorkItemRefCard state={state} onOpenIssue={onOpenIssue} />;
}
